import { CpuIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { PeriodControls } from '@/features/workspace-usage/components/PeriodControls';
import { EmptyState } from '@/shared/components/EmptyState';
import { PageHeader } from '@/shared/components/PageHeader';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import type { AiModelsView } from '../aiModelsSearch';
import { SegmentedControl } from '@/shared/components/SegmentedControl';
import { ledgerRows, mapRows, profileIdentities } from '../modelsLedger';
// By path, not the feature's index: the index also exports the admin sections, and
// they would ride into this page's chunk.
import { useAiProviders } from '@/features/ai-providers/hooks/useAiProviders';
import { useWorkspaceAiModels } from '../hooks/useWorkspaceAiModels';
import { AiModelsSkeleton } from './AiModelsSkeleton';
import { ProfileLedger } from './ProfileLedger';
import { RunsShareCard } from './RunsShareCard';
import { StepMap } from './StepMap';

/** The page frame, as `UsagePage` draws its own. */
const PAGE_SECTION =
  'flex flex-1 flex-col gap-6 max-md:px-4 max-md:pt-4 p-6 pb-12';

const HEADER = {
  eyebrow: 'Workspace',
  title: 'AI models',
  description:
    'Which AI models this workspace’s workflows use, and how much they ran.',
};

export interface AiModelsPageProps {
  workspaceId: string;
  /** The period being read, from the address. Absent means the current month. */
  period?: string;
  onPeriodChange: (next: string | undefined) => void;
  /** Which view, from the address (spec 221). */
  view?: AiModelsView;
  onViewChange?: (next: AiModelsView) => void;
}

/**
 * A workspace's AI models (specs 215, 233): for everyone who can open the workspace,
 * how the period's runs split between profiles, then either the profiles side by
 * side (By profile) or each workflow's steps and the model each runs on (By
 * workflow). Runs and tokens only; never money.
 */
export function AiModelsPage({
  workspaceId,
  period,
  onPeriodChange,
  view = 'profile',
  onViewChange,
}: AiModelsPageProps) {
  const models = useWorkspaceAiModels(workspaceId, period);
  // Each profile's AI connection by name (spec 222). Read alongside, never waited on:
  // a list that is slow or refused only leaves the "via" out.
  const providers = useAiProviders();
  const showPlaceholder = usePendingDisplay(models.isPending);

  if (models.isPending) {
    return (
      <section className={PAGE_SECTION}>
        <PageHeader {...HEADER} />
        {showPlaceholder ? <AiModelsSkeleton /> : null}
      </section>
    );
  }

  if (models.isError) {
    return (
      <section className={PAGE_SECTION}>
        <PageHeader {...HEADER} />
        <EmptyState
          icon={CpuIcon}
          title="The AI models could not be loaded"
          description="This page could not be read just now."
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={() => void models.refetch()}
              disabled={models.isFetching}
            >
              {models.isFetching ? 'Trying…' : 'Try again'}
            </Button>
          }
        />
      </section>
    );
  }

  const { data } = models;
  const identities = profileIdentities(data.entries);
  const ledger = ledgerRows(data.entries, identities);
  const workflows = mapRows(data.workflows, identities);
  const connectionNameOf = (aiProviderId: string) =>
    providers.entries?.find((provider) => provider.id === aiProviderId)?.name;
  const byProfile = view === 'profile';
  const profileCount = ledger.filter((row) => row.profile !== null).length;
  return (
    <section className={PAGE_SECTION}>
      <PageHeader
        {...HEADER}
        actions={
          // Both views read the period now (spec 233): the map ends each row with
          // its runs. Under AI usage these stand in the toolbar under the tabs.
          <PeriodControls
            period={data.period}
            earliestPeriodKey={data.earliest_period_key}
            onChange={onPeriodChange}
          />
        }
        aside={
          // A view of this page, not another page: a segmented switch rather than a
          // second tab strip under the section's (spec 224 review).
          data.entries.length > 0 ? (
            <SegmentedControl
              label="View"
              value={view}
              onChange={(next) => onViewChange?.(next)}
              options={[
                { value: 'profile', label: 'By profile', count: profileCount },
                {
                  value: 'workflow',
                  label: 'By workflow',
                  count: workflows.length,
                },
              ]}
            />
          ) : undefined
        }
      />
      {data.entries.length === 0 ? (
        <EmptyState
          icon={CpuIcon}
          title="No workflow in this workspace uses an AI model yet"
          description="When a workflow has an AI step, its profile shows here."
        />
      ) : (
        <div className="flex flex-col gap-4">
          <RunsShareCard
            entries={data.entries}
            period={data.period}
            identities={identities}
          />
          {byProfile ? (
            <ProfileLedger
              rows={ledger}
              period={data.period}
              workspaceId={workspaceId}
              connectionNameOf={connectionNameOf}
            />
          ) : (
            <StepMap
              rows={workflows}
              legend={ledger.flatMap((row) =>
                row.profile
                  ? [
                      {
                        key: row.key,
                        name: row.name,
                        model: row.profile.model,
                        slot: row.identity.slot,
                        idle: row.identity.idle,
                      },
                    ]
                  : [],
              )}
              workspaceId={workspaceId}
              period={data.period}
            />
          )}
        </div>
      )}
    </section>
  );
}
